// A rated movie: poster, title, the person's stars. Used by the profile's
// Recently Rated rail and the full ratings history. Read-only — ratings are
// edited on the movie page (Phase 2A control), never here.
import { Link } from 'react-router-dom';
import { formatRating, ratingAccessibilityText, starFills } from '@aluvo/constants/src/ratings';
import styles from './RatedMovies.module.css';

const STAR_PATH = 'M12 2.6l2.93 5.94 6.56.95-4.75 4.63 1.12 6.53L12 17.57l-5.86 3.08 1.12-6.53L2.51 9.49l6.56-.95L12 2.6z';

/** Five small read-only stars with the number, e.g. ★★★★½ 4.5 */
export function StarDisplay({ rating, className = '' }) {
    return (
        <span className={`${styles.stars} ${className}`} role="img" aria-label={`Rated ${ratingAccessibilityText(rating)} out of 5`}>
            {starFills(rating).map((fill, i) => (
                <span key={i} className={styles.star} data-fill={fill} aria-hidden="true">
                    <svg viewBox="0 0 24 24" className={styles.starOutline}><path d={STAR_PATH} /></svg>
                    {fill !== 'empty' && (
                        <span className={fill === 'half' ? styles.starHalf : styles.starFull}>
                            <svg viewBox="0 0 24 24" className={styles.starSolid}><path d={STAR_PATH} /></svg>
                        </span>
                    )}
                </span>
            ))}
            <span className={styles.starNumber} aria-hidden="true">{formatRating(rating)}</span>
        </span>
    );
}

export const movieDetailPath = (item) => `/movie/movie/${item.tmdbId}`;

const ratedOn = (iso) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * variant 'rail' (compact poster card) or 'row' (history list: poster, title,
 * year, stars, date).
 */
export default function RatedMovieCard({ item, variant = 'rail', onOpen }) {
    const poster = item.posterUrl
        ? <img src={item.posterUrl} alt="" className={styles.poster} loading="lazy" />
        : <span className={`${styles.poster} ${styles.posterFallback}`} aria-hidden="true">🎬</span>;

    if (variant === 'row') {
        const date = ratedOn(item.ratedAt);
        return (
            <li className={styles.row}>
                <Link to={movieDetailPath(item)} className={styles.rowLink} onClick={onOpen}>
                    {poster}
                    <span className={styles.rowBody}>
                        <span className={styles.rowTitle}>{item.title}</span>
                        {item.year && <span className={styles.meta}>{item.year}</span>}
                        <StarDisplay rating={item.rating} />
                        {date && <span className={styles.meta}>Rated {date}</span>}
                    </span>
                </Link>
            </li>
        );
    }

    return (
        <li className={styles.railItem}>
            <Link to={movieDetailPath(item)} className={styles.railLink} onClick={onOpen} aria-label={`${item.title}, rated ${ratingAccessibilityText(item.rating)}`}>
                {poster}
                <span className={styles.railTitle} aria-hidden="true">{item.title}</span>
                <span aria-hidden="true"><StarDisplay rating={item.rating} /></span>
            </Link>
        </li>
    );
}
